<div class="ab-wrap" data-testid="avatar-builder">
  <!-- PREVIEW -->
  <div
    class="ab-preview"
    role="img"
    aria-label="Avatar preview"
    aria-live="polite"
    aria-atomic="true"
  >
    <canvas #avatarCanvas aria-hidden="true" data-testid="avatar-canvas"></canvas>
  </div>

  <!-- STEP DOTS -->
  <div class="ab-steps" role="list" aria-label="Avatar builder steps">
    @for (s of [1, 2, 3, 4, 5, 6, 7]; track s) {
      <div
        class="ab-step-dot"
        [class.active]="currentStep === s"
        (click)="currentStep = s"
        role="button"
        tabindex="0"
        [attr.aria-label]="'Go to step ' + s"
        [attr.aria-current]="currentStep === s ? 'step' : null"
        [attr.data-testid]="'avatar-step-dot-' + s"
        (keydown.enter)="currentStep = s"
        (keydown.space)="currentStep = s; $event.preventDefault()"
      ></div>
    }
  </div>

  <!-- OPTIONS -->
  <div class="ab-options">
    <!-- STEP 1: Style + Skin -->
    @if (currentStep === 1) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Skin tone</div>

      <div class="ab-section">
        <div class="ab-label">Skin tone</div>

        <div class="ab-swatches" role="list" aria-label="Skin tone options">
          @for (s of skins; track s) {
            <button
              class="ab-swatch"
              [class.active]="config.skin === s"
              [style.background]="skinPalettes[s].base"
              (click)="config.skin = s; onOptionChange()"
              [attr.aria-label]="'Select skin tone ' + s"
              [attr.aria-pressed]="config.skin === s"
              [attr.data-testid]="'avatar-skin-' + s"
            ></button>
          }
        </div>
      </div>

      <div class="ab-section">
        <div class="ab-label">Rosy cheeks</div>

        <mat-button-toggle-group
          [(ngModel)]="config.cheeks"
          (change)="onOptionChange()"
          class="ab-toggle-group"
          aria-label="Toggle rosy cheeks"
          data-testid="avatar-cheeks-toggle"
        >
          <mat-button-toggle [value]="true">On</mat-button-toggle>
          <mat-button-toggle [value]="false">Off</mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    }

    <!-- STEP 2: Hair -->
    @if (currentStep === 2) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Hair</div>

      <div class="ab-section">
        <div class="ab-label">Hair color</div>

        <div class="ab-swatches" role="list" aria-label="Hair color options">
          @for (h of hairColors; track h) {
            <button
              class="ab-swatch"
              [class.active]="config.hairColor === h"
              [style.background]="hairColorSwatches[h]"
              [style.border-color]="h === 'white' ? '#ccc' : 'transparent'"
              (click)="config.hairColor = h; onOptionChange()"
              [attr.aria-label]="'Select hair color ' + h"
              [attr.aria-pressed]="config.hairColor === h"
              [attr.data-testid]="'avatar-hair-color-' + h"
            ></button>
          }
        </div>
      </div>

      <div class="ab-section">
        <div class="ab-label">Front style</div>

        <div class="ab-pills" role="list" aria-label="Front hair style options">
          @for (f of hairFronts; track f.id) {
            <button
              mat-flat-button
              class="ab-pill"
              [class.active]="config.hairFront === f.id"
              (click)="config.hairFront = f.id; onOptionChange()"
              [attr.aria-label]="'Select front hair style ' + f.label"
              [attr.aria-pressed]="config.hairFront === f.id"
              [attr.data-testid]="'avatar-hair-front-' + f.id"
            >
              {{ f.label }}
            </button>
          }
        </div>
      </div>

      <div class="ab-section">
        <div class="ab-label">Back style</div>

        <div class="ab-pills" role="list" aria-label="Back hair style options">
          @for (b of hairBacks; track b.id) {
            <button
              mat-flat-button
              class="ab-pill"
              [class.active]="config.hairBack === b.id"
              (click)="config.hairBack = b.id; onOptionChange()"
              [attr.aria-label]="'Select back hair style ' + b.label"
              [attr.aria-pressed]="config.hairBack === b.id"
              [attr.data-testid]="'avatar-hair-back-' + b.id"
            >
              {{ b.label }}
            </button>
          }
        </div>
      </div>
    }

    <!-- STEP 3: Face -->
    @if (currentStep === 3) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Expression</div>

      <div class="ab-section">
        <div class="ab-label">Expression</div>

        <div class="ab-pills" role="list" aria-label="Face expression options">
          @for (f of faces; track f.id) {
            <button
              mat-flat-button
              class="ab-pill"
              [class.active]="config.face === f.id"
              (click)="config.face = f.id; onOptionChange()"
              [attr.aria-label]="'Select expression ' + f.label"
              [attr.aria-pressed]="config.face === f.id"
              [attr.data-testid]="'avatar-face-' + f.id"
            >
              {{ f.label }}
            </button>
          }
        </div>
      </div>
    }

    <!-- STEP 4: Shirt -->
    @if (currentStep === 4) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Top</div>

      <div class="ab-section">
        <div class="ab-label">Style</div>

        <mat-button-toggle-group
          [(ngModel)]="config.shirtStyle"
          (change)="onOptionChange()"
          class="ab-toggle-group"
          aria-label="Select top style"
          data-testid="avatar-shirt-style-toggle"
        >
          @for (s of shirtStyles; track s.id) {
            <mat-button-toggle [value]="s.id">{{ s.label }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>

      <div class="ab-section">
        <div class="ab-label">Color</div>

        <div class="ab-swatches" role="list" aria-label="Top color options">
          @for (s of shirtColors; track s) {
            <button
              class="ab-swatch"
              [class.active]="config.shirtColor === s"
              [style.background]="shirtColorSwatches[s]"
              [style.border-color]="s === 'white' ? '#ccc' : 'transparent'"
              (click)="config.shirtColor = s; onOptionChange()"
              [attr.aria-label]="'Select top color ' + s"
              [attr.aria-pressed]="config.shirtColor === s"
              [attr.data-testid]="'avatar-shirt-color-' + s"
            ></button>
          }
        </div>
      </div>

      <div class="ab-section">
        <div class="ab-label">Sleeves</div>

        <mat-button-toggle-group
          [(ngModel)]="config.sleeveLength"
          (change)="onOptionChange()"
          class="ab-toggle-group"
          aria-label="Select sleeve length"
          data-testid="avatar-sleeve-toggle"
        >
          @for (s of sleeveLengths; track s.id) {
            <mat-button-toggle [value]="s.id">{{ s.label }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>
    }

    <!-- STEP 5: Pants -->
    @if (currentStep === 5) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Bottoms</div>

      <div class="ab-section">
        <div class="ab-label">Style</div>

        <mat-button-toggle-group
          [(ngModel)]="config.pantsStyle"
          (change)="onOptionChange()"
          class="ab-toggle-group"
          aria-label="Select bottoms style"
          data-testid="avatar-pants-style-toggle"
        >
          @for (p of pantsStyles; track p.id) {
            <mat-button-toggle [value]="p.id">{{ p.label }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>

      @if (config.pantsStyle !== 'naked') {
        <div class="ab-section">
          <div class="ab-label">Color</div>

          <div class="ab-swatches" role="list" aria-label="Bottoms color options">
            @for (p of pantsColors; track p) {
              <button
                class="ab-swatch"
                [class.active]="config.pantsColor === p"
                [style.background]="pantsColorSwatches[p]"
                (click)="config.pantsColor = p; onOptionChange()"
                [attr.aria-label]="'Select bottoms color ' + p"
                [attr.aria-pressed]="config.pantsColor === p"
                [attr.data-testid]="'avatar-pants-color-' + p"
              ></button>
            }
          </div>
        </div>
      }
    }

    <!-- STEP 6: Glasses & beard -->
    @if (currentStep === 6) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Glasses & beard</div>

      <div class="ab-section">
        <div class="ab-label">Glasses</div>

        <div class="ab-pills" role="list" aria-label="Glasses options">
          @for (g of glassesOptions; track g.id) {
            <button
              class="ab-pill"
              [class.active]="config.glasses === g.id"
              (click)="config.glasses = g.id; onOptionChange()"
              [attr.aria-label]="'Select glasses ' + g.label"
              [attr.aria-pressed]="config.glasses === g.id"
              [attr.data-testid]="'avatar-glasses-' + g.id"
            >
              {{ g.label }}
            </button>
          }
        </div>
      </div>

      <div class="ab-section">
        <div class="ab-label">Beard</div>

        <div class="ab-pills" role="list" aria-label="Beard options">
          @for (b of beardOptions; track b.id) {
            <button
              mat-flat-button
              class="ab-pill"
              [class.active]="config.beard === b.id"
              (click)="config.beard = b.id; onOptionChange()"
              [attr.aria-label]="'Select beard ' + b.label"
              [attr.aria-pressed]="config.beard === b.id"
              [attr.data-testid]="'avatar-beard-' + b.id"
            >
              {{ b.label }}
            </button>
          }
        </div>
      </div>
    }

    <!-- STEP 7: Career accessory -->
    @if (currentStep === 7) {
      <div class="ab-step-title" aria-live="polite" aria-atomic="true">Accessory</div>

      <div class="ab-section">
        <div class="ab-label">Career accessories</div>

        <div class="ab-pills" role="list" aria-label="Career accessory options">
          @for (a of accessories; track a.id) {
            <button
              mat-flat-button
              class="ab-pill"
              [class.active]="config.accessory === a.id"
              (click)="config.accessory = a.id; onOptionChange()"
              [attr.aria-label]="'Select accessory ' + a.label"
              [attr.aria-pressed]="config.accessory === a.id"
              [attr.data-testid]="'avatar-accessory-' + a.id"
            >
              {{ a.icon }} {{ a.label }}
            </button>
          }
        </div>
      </div>
    }
  </div>

  <!-- FOOTER -->
  <div class="ab-footer">
    <button
      mat-stroked-button
      class="btn-outline"
      (click)="currentStep > 1 ? (currentStep = currentStep - 1) : cancel()"
      [attr.aria-label]="currentStep === 1 ? 'Cancel avatar creation' : 'Go to previous step'"
      data-testid="avatar-back-btn"
    >
      {{ currentStep === 1 ? 'Cancel' : 'Back' }}
    </button>

    @if (currentStep < 7) {
      <button
        mat-flat-button
        class="btn-primary"
        (click)="currentStep = currentStep + 1"
        aria-label="Go to next step"
        data-testid="avatar-next-btn"
      >
        Next
      </button>
    }

    @if (currentStep === 7) {
      <button
        mat-flat-button
        class="btn-primary"
        (click)="save()"
        aria-label="Save avatar"
        data-testid="avatar-save-btn"
      >
        Save avatar
      </button>
    }
  </div>
</div>
